import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../services/api';
import { questions } from '../data/questions';
import AdminDirectChat from '../components/AdminDirectChat';

export default function AdminUserDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [chatExpanded, setChatExpanded] = useState(false);
  const [resumeIdx, setResumeIdx] = useState(0);
  const [tokenAmount, setTokenAmount] = useState('');
  const [tokenNote, setTokenNote] = useState('');
  const [grantLoading, setGrantLoading] = useState(false);
  const [grantMsg, setGrantMsg] = useState('');
  const [grantError, setGrantError] = useState('');
  const [disableLoading, setDisableLoading] = useState(false);
  const [assessment, setAssessment] = useState(undefined); // undefined = not loaded, null = no data
  const [editMode, setEditMode] = useState(false);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editLinkedin, setEditLinkedin] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [editMsg, setEditMsg] = useState('');
  const [answersExpanded, setAnswersExpanded] = useState(false);
  const [interviewSessions, setInterviewSessions] = useState([]);
  const [interviewExpanded, setInterviewExpanded] = useState(null); // session ID
  const [cdpData, setCdpData] = useState(undefined); // undefined = loading, null = no data
  const [cdpExpanded, setCdpExpanded] = useState(null); // enrolment ID

  const loadUser = () => {
    setLoading(true);
    api.get(`/admin/users/${id}`)
      .then(({ data }) => setData(data))
      .catch(() => setError('Failed to load user'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadUser();
    api.get(`/assessment/admin/user/${id}`)
      .then(({ data }) => setAssessment(data.assessment ? data : null))
      .catch(() => setAssessment(null));
    api.get(`/admin/users/${id}/mock-interviews`)
      .then(({ data }) => setInterviewSessions(data.sessions || []))
      .catch(() => setInterviewSessions([]));
    api.get(`/cdp/admin/user/${id}/cdp-data`)
      .then(({ data }) => setCdpData(data.enrolments?.length || data.certificates?.length ? data : null))
      .catch(() => setCdpData(null));
  }, [id]);

  const handleGrantTokens = async (e) => {
    e.preventDefault();
    const amt = parseInt(tokenAmount, 10);
    if (!amt || amt < 1) return setGrantError('Enter a valid token amount');
    setGrantLoading(true); setGrantMsg(''); setGrantError('');
    try {
      const { data: res } = await api.post(`/admin/users/${id}/tokens`, {
        amount: amt,
        note: tokenNote.trim(),
      });
      setGrantMsg(`Granted ${amt.toLocaleString()} tokens. New balance: ${res.newBalance.toLocaleString()}`);
      setTokenAmount('');
      setTokenNote('');
      loadUser(); // Refresh user data and token txns
    } catch (err) {
      setGrantError(err.response?.data?.error || 'Failed to grant tokens');
    } finally {
      setGrantLoading(false);
    }
  };

  const handleToggleDisable = async () => {
    const action = data.user.isDisabled ? 'enable' : 'disable';
    if (!data.user.isDisabled && !window.confirm('Are you sure you want to disable this account? The user will not be able to access the platform.')) return;
    setDisableLoading(true);
    try {
      await api.put(`/admin/users/${id}/${action}`);
      loadUser();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} account`);
    } finally {
      setDisableLoading(false);
    }
  };

  const startEdit = () => {
    setEditName(user?.name || '');
    setEditEmail(user?.email || '');
    setEditLinkedin(user?.linkedin || '');
    setEditMsg('');
    setEditMode(true);
  };

  const handleSaveProfile = async () => {
    setEditSaving(true); setEditMsg('');
    try {
      await api.put(`/admin/users/${id}/profile`, { name: editName, email: editEmail, linkedin: editLinkedin });
      setEditMsg('Profile updated');
      setEditMode(false);
      loadUser();
    } catch (err) {
      setEditMsg(err.response?.data?.error || 'Failed to update');
    } finally { setEditSaving(false); }
  };

  if (loading) return <div style={s.center}><div style={s.spinner} /></div>;
  if (error) return <div style={s.center}><p style={{ color: 'var(--danger)' }}>{error}</p></div>;

  const { user, resumes, chats, tokenTxns = [] } = data;
  const initials = (user.name?.[0] || user.phone?.slice(-2) || 'U').toUpperCase();
  const resume = resumes[resumeIdx];
  const visibleChats = chatExpanded ? chats : chats.slice(0, 5);
  const balanceLow = user.tokenBalance < 2000;
  const balanceEmpty = user.tokenBalance <= 0;

  return (
    <div style={s.page} className="page-content">
      {/* Header */}
      <div style={s.header}>
        <button onClick={() => navigate('/admin')} style={s.back}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
          Back to Admin
        </button>
        <span style={s.headerBadge}>User Profile</span>
      </div>

      {/* Profile card */}
      <div style={s.profileCard}>
        <div style={s.profileBg} />
        <div style={s.profileContent}>
          <div style={s.avatar}>{initials}</div>
          <div style={s.profileInfo}>
            <h1 style={s.profileName}>{user.name || 'No name set'}</h1>
            <a href={`https://wa.me/${user.phone?.replace(/\+/g, '')}`} target="_blank" rel="noopener noreferrer" style={{ ...s.profilePhone, textDecoration: 'underline', cursor: 'pointer' }}>{user.phone}</a>
            {user.email && <p style={s.profilePhone}>{user.email}</p>}
            {user.linkedin && <p style={s.profilePhone}><a href={user.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-3)', textDecoration: 'underline' }}>{user.linkedin}</a></p>}
            <div style={s.badgeRow}>
              <span style={{ ...s.roleBadge, ...roleColor(user.role) }}>{user.role}</span>
              {user.isDisabled && <span style={s.disabledBadge}>Disabled</span>}
              <span style={s.joinedBadge}>
                Joined {new Date(user.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '4px' }}>
              <button onClick={startEdit} style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: '6px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' }}>Edit Profile</button>
              <button
                onClick={handleToggleDisable}
                disabled={disableLoading}
                style={user.isDisabled ? s.enableAccountBtn : s.disableAccountBtn}
              >
                {disableLoading ? 'Processing...' : user.isDisabled ? 'Enable Account' : 'Disable Account'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Edit profile form */}
      {editMode && (
        <div style={{ background: '#fff', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', marginBottom: '16px' }}>
          <h3 style={{ fontSize: '14px', fontWeight: '700', color: 'var(--navy)', margin: '0 0 12px' }}>Edit Profile</h3>
          <div style={{ display: 'grid', gap: '10px' }}>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)', display: 'block', marginBottom: '4px' }}>Name</label>
              <input value={editName} onChange={e => setEditName(e.target.value)} style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: "'DM Sans',sans-serif", outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)', display: 'block', marginBottom: '4px' }}>Email</label>
              <input value={editEmail} onChange={e => setEditEmail(e.target.value)} type="email" style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: "'DM Sans',sans-serif", outline: 'none', boxSizing: 'border-box' }} />
            </div>
            <div>
              <label style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy)', display: 'block', marginBottom: '4px' }}>LinkedIn</label>
              <input value={editLinkedin} onChange={e => setEditLinkedin(e.target.value)} placeholder="username or full URL" style={{ width: '100%', padding: '9px 12px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '13px', fontFamily: "'DM Sans',sans-serif", outline: 'none', boxSizing: 'border-box' }} />
            </div>
          </div>
          {editMsg && <p style={{ fontSize: '12px', color: editMsg.includes('updated') ? '#065f46' : '#991b1b', marginTop: '8px' }}>{editMsg}</p>}
          <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
            <button onClick={handleSaveProfile} disabled={editSaving} style={{ background: 'var(--navy)', color: '#fff', border: 'none', padding: '8px 18px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', opacity: editSaving ? 0.5 : 1 }}>{editSaving ? 'Saving...' : 'Save'}</button>
            <button onClick={() => setEditMode(false)} style={{ background: '#fff', border: '1px solid var(--border)', padding: '8px 18px', borderRadius: '8px', fontSize: '12px', fontWeight: '600', cursor: 'pointer', color: 'var(--text-2)' }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Stats row */}
      <div style={s.statsRow}>
        {[
          { icon: '💬', label: 'Total Queries', value: (user.totalQueries || 0).toLocaleString() },
          { icon: '⚡', label: 'Tokens Used', value: (user.tokensUsed || 0).toLocaleString() },
          { icon: '📄', label: 'Resumes', value: resumes.length },
          { icon: '🗒️', label: 'Chats Stored', value: chats.length },
        ].map((stat) => (
          <div key={stat.label} style={s.statCard}>
            <span style={s.statIcon}>{stat.icon}</span>
            <div>
              <p style={s.statLabel}>{stat.label}</p>
              <p style={s.statVal}>{stat.value}</p>
            </div>
          </div>
        ))}
      </div>

      {/* Direct Chat */}
      <AdminDirectChat userId={id} userName={user.name || user.phone} />

      {/* Resume section */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <div style={s.cardHeaderLeft}>
            <h2 style={s.cardTitle}>Resumes</h2>
            <span style={s.countBadge}>{resumes.length}</span>
          </div>
          {resumes.length > 1 && (
            <div style={s.resumeTabs}>
              {resumes.map((r, i) => (
                <button key={r._id} onClick={() => setResumeIdx(i)} style={{ ...s.resumeTab, ...(i === resumeIdx ? s.resumeTabActive : {}) }}>
                  {r.title || `Resume ${i + 1}`}
                </button>
              ))}
            </div>
          )}
        </div>

        {resumes.length === 0 ? (
          <p style={s.emptyText}>No resumes created yet.</p>
        ) : (
          <div style={s.resumeBody}>
            <div style={s.resumeSection}>
              <h3 style={s.resumeSectionTitle}>Personal Information</h3>
              <div style={s.infoGrid}>
                {[
                  { label: 'Full Name', value: resume.personalInfo?.fullName },
                  { label: 'Email', value: resume.personalInfo?.email },
                  { label: 'Phone', value: resume.personalInfo?.phone },
                  { label: 'Location', value: resume.personalInfo?.location },
                  { label: 'LinkedIn', value: resume.personalInfo?.linkedin },
                  { label: 'Website', value: resume.personalInfo?.website },
                ].filter(f => f.value).map(({ label, value }) => (
                  <div key={label} style={s.infoItem}>
                    <span style={s.infoLabel}>{label}</span>
                    <span style={s.infoVal}>{value}</span>
                  </div>
                ))}
              </div>
            </div>

            {resume.summary && (
              <div style={s.resumeSection}>
                <h3 style={s.resumeSectionTitle}>Summary</h3>
                <p style={s.summaryText}>{resume.summary}</p>
              </div>
            )}

            {resume.experience?.length > 0 && (
              <div style={s.resumeSection}>
                <h3 style={s.resumeSectionTitle}>Experience</h3>
                {resume.experience.map((exp, i) => (
                  <div key={i} style={s.expCard}>
                    <div style={s.expHeader}>
                      <div>
                        <p style={s.expPosition}>{exp.position}</p>
                        <p style={s.expCompany}>{exp.company}</p>
                      </div>
                      <span style={s.expDates}>{exp.startDate} – {exp.current ? 'Present' : exp.endDate}</span>
                    </div>
                    {exp.description && <p style={s.expDesc}>{exp.description}</p>}
                  </div>
                ))}
              </div>
            )}

            {resume.education?.length > 0 && (
              <div style={s.resumeSection}>
                <h3 style={s.resumeSectionTitle}>Education</h3>
                {resume.education.map((edu, i) => (
                  <div key={i} style={s.expCard}>
                    <div style={s.expHeader}>
                      <div>
                        <p style={s.expPosition}>{edu.degree} {edu.field ? `in ${edu.field}` : ''}</p>
                        <p style={s.expCompany}>{edu.institution}</p>
                      </div>
                      <span style={s.expDates}>{edu.startDate} – {edu.endDate}</span>
                    </div>
                    {edu.grade && <p style={s.expDesc}>Grade: {edu.grade}</p>}
                  </div>
                ))}
              </div>
            )}

            {resume.skills?.length > 0 && (
              <div style={s.resumeSection}>
                <h3 style={s.resumeSectionTitle}>Skills</h3>
                <div style={s.skillsWrap}>
                  {resume.skills.map((skill, i) => (
                    <span key={i} style={s.skillTag}>{skill}</span>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Assessment section */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <div style={s.cardHeaderLeft}>
            <h2 style={s.cardTitle}>Practice Area Assessment</h2>
            {assessment?.assessment && <span style={s.countBadge}>Completed</span>}
          </div>
          {assessment?.assessment?.result?.completedAt && (
            <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
              {new Date(assessment.assessment.result.completedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
            </span>
          )}
        </div>

        {assessment === undefined && <p style={s.emptyText}>Loading...</p>}
        {assessment === null && <p style={s.emptyText}>No completed assessment.</p>}
        {assessment?.assessment && (() => {
          const { result, answers } = assessment.assessment;
          const { outcomeData } = assessment;
          const answersMap = {};
          (answers || []).forEach(a => { answersMap[a.questionNumber] = a.selectedOption; });
          return (
            <div style={s.assessBody}>
              {/* Result summary */}
              <div style={s.assessResult}>
                <div style={s.assessResultLeft}>
                  <p style={s.assessOutcomeCode}>{result.outcomeCode}</p>
                  <p style={s.assessTitle}>{result.outcomeTitle}</p>
                  <p style={s.assessArea}>{result.practiceArea}</p>
                </div>
                <div style={s.assessScores}>
                  {[
                    { label: 'Corporate', val: result.corpScore },
                    { label: 'Litigation', val: result.litScore },
                    { label: 'In-House', val: result.ihScore },
                    { label: 'Readiness', val: result.readinessScore },
                  ].map(({ label, val }) => (
                    <div key={label} style={s.assessScoreItem}>
                      <span style={s.assessScoreVal}>{val}</span>
                      <span style={s.assessScoreLabel}>{label}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Full outcome details */}
              {outcomeData && (
                <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border-2)' }}>
                  {outcomeData.description && (
                    <p style={{ fontSize: '13px', color: 'var(--text-2)', lineHeight: '1.65', marginBottom: '12px' }}>{outcomeData.description}</p>
                  )}
                  {outcomeData.message && (
                    <div style={{ background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: '10px', padding: '12px 16px', marginBottom: '12px' }}>
                      <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>From Nitin</p>
                      <p style={{ fontSize: '13px', color: 'var(--text)', lineHeight: '1.6', fontStyle: 'italic' }}>{outcomeData.message}</p>
                    </div>
                  )}
                  {result.specialistNoteTriggered && outcomeData.specialistNote && (
                    <div style={{ background: 'rgba(217,119,6,0.06)', border: '1px solid rgba(217,119,6,0.2)', borderRadius: '10px', padding: '12px 16px', marginBottom: '12px' }}>
                      <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--warning)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Specialist Note</p>
                      <p style={{ fontSize: '13px', color: 'var(--text)', lineHeight: '1.6' }}>{outcomeData.specialistNote}</p>
                    </div>
                  )}
                  {outcomeData.steps && outcomeData.steps.length > 0 && (
                    <div>
                      <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Next Steps</p>
                      {outcomeData.steps.map((step, i) => (
                        <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '6px' }}>
                          <span style={{ fontSize: '10px', fontWeight: '800', color: '#fff', background: 'var(--navy)', borderRadius: '50%', width: '20px', height: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px' }}>{i + 1}</span>
                          <p style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.55' }}>{step}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Q&A list — collapsible */}
              <div style={s.qaSection}>
                <div
                  onClick={() => setAnswersExpanded(v => !v)}
                  style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', cursor: 'pointer', userSelect: 'none' }}
                >
                  <p style={{ ...s.qaSectionTitle, padding: 0, margin: 0 }}>Question-by-Question Answers</p>
                  <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy-2)' }}>{answersExpanded ? 'Hide ↑' : 'View Answers ↓'}</span>
                </div>
                {answersExpanded && questions.map(q => {
                  const selected = answersMap[q.id];
                  const selectedOpt = selected ? q.options.find(o => o.id === selected) : null;
                  return (
                    <div key={q.id} style={s.qaRow}>
                      <div style={s.qaNumWrap}>
                        <span style={s.qaNum}>Q{q.id}</span>
                      </div>
                      <div style={s.qaContent}>
                        <p style={s.qaQuestion}>{q.versions ? q.versions[0] : q.question}</p>
                        {selectedOpt ? (
                          <div style={s.qaAnswer}>
                            <span style={s.qaAnswerId}>{selectedOpt.id}</span>
                            <span style={s.qaAnswerText}>{selectedOpt.versions ? selectedOpt.versions[0] : selectedOpt.text}</span>
                          </div>
                        ) : (
                          <p style={s.qaNoAnswer}>Not answered</p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}
      </div>

      {/* Mock Interview Sessions */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <div style={s.cardHeaderLeft}>
            <h2 style={s.cardTitle}>Mock Interviews</h2>
            <span style={s.countBadge}>{interviewSessions.length}</span>
          </div>
        </div>

        {interviewSessions.length === 0 ? (
          <p style={s.emptyText}>No mock interview sessions.</p>
        ) : (
          <div style={{ padding: '0' }}>
            {interviewSessions.map(session => {
              const isExpanded = interviewExpanded === session._id;
              const catLabel = session.category === 'A' ? 'Internship' : session.category === 'B' ? 'First Job or PPO' : 'Experienced';
              const fmtD = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
              return (
                <div key={session._id} style={{ borderBottom: '1px solid var(--border-2)' }}>
                  <div
                    onClick={() => setInterviewExpanded(isExpanded ? null : session._id)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ ...s.roleBadge, background: session.status === 'completed' ? '#d1fae5' : '#dbeafe', color: session.status === 'completed' ? '#065f46' : '#1e40af', fontSize: '10px' }}>
                        {session.status === 'completed' ? 'Completed' : 'In Progress'}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)' }}>{catLabel}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>{fmtD(session.createdAt)}</span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy-2)' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '0 20px 16px' }}>
                      {/* Conversation */}
                      <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Conversation</p>
                      {session.messages?.map((msg, i) => (
                        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '8px', alignItems: 'flex-start' }}>
                          <span style={{
                            fontSize: '9px', fontWeight: '800', width: '18px', height: '18px', borderRadius: '5px',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px',
                            ...(msg.role === 'assistant'
                              ? { background: 'var(--navy)', color: 'var(--gold-mid)' }
                              : { background: 'var(--bg)', color: 'var(--text-3)', border: '1px solid var(--border)' })
                          }}>
                            {msg.role === 'assistant' ? 'N' : 'S'}
                          </span>
                          <p style={{ flex: 1, fontSize: '12px', color: msg.role === 'assistant' ? 'var(--navy)' : 'var(--text-2)', lineHeight: '1.6', fontWeight: msg.role === 'assistant' ? '600' : '400' }}>
                            {msg.content}
                          </p>
                        </div>
                      ))}

                      {/* Closing Note */}
                      {session.closingNote && (
                        <div style={{ marginTop: '12px' }}>
                          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Closing Note</p>
                          <div style={{ background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: '10px', padding: '14px 16px', fontSize: '13px', color: 'var(--text)', lineHeight: '1.7', whiteSpace: 'pre-wrap' }}>
                            {session.closingNote}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* CDP Programme */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <div style={s.cardHeaderLeft}>
            <h2 style={s.cardTitle}>CDP Programme</h2>
            {cdpData && <span style={s.countBadge}>{cdpData.enrolments?.length || 0} enrolments</span>}
          </div>
        </div>

        {cdpData === undefined && <p style={s.emptyText}>Loading...</p>}
        {cdpData === null && <p style={s.emptyText}>No CDP activity.</p>}
        {cdpData && (
          <div>
            {/* Certificates */}
            {cdpData.certificates?.length > 0 && (
              <div style={{ padding: '12px 20px', borderBottom: '1px solid var(--border-2)', background: 'var(--gold-light)' }}>
                <p style={{ fontSize: '10px', fontWeight: '800', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>Certificates</p>
                {cdpData.certificates.map(cert => (
                  <div key={cert._id} style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                    <span style={{
                      fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px',
                      background: cert.level === 'gold' ? '#fef3c7' : cert.level === 'silver' ? '#e5e7eb' : '#dbeafe',
                      color: cert.level === 'gold' ? '#92400e' : cert.level === 'silver' ? '#374151' : '#1e40af',
                      textTransform: 'capitalize',
                    }}>{cert.level}</span>
                    <span style={{ fontSize: '12px', color: 'var(--text-2)', fontWeight: '600' }}>{cert.certificateNumber}</span>
                    <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                      {new Date(cert.issuedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Enrolments */}
            {cdpData.enrolments?.map(enr => {
              const isExpanded = cdpExpanded === enr._id;
              const docTitle = enr.documentId?.title || 'Unknown Document';
              const scenarioCode = enr.scenarioId?.code || '';
              const statusColors = {
                passed: { bg: '#d1fae5', color: '#065f46' },
                assessed: { bg: '#dbeafe', color: '#1e40af' },
                assessing: { bg: '#fef3c7', color: '#92400e' },
                resubmit: { bg: '#fee2e2', color: '#991b1b' },
                draft_uploaded: { bg: '#e0e7ff', color: '#3730a3' },
                enrolled: { bg: '#f3f4f6', color: '#374151' },
              };
              const sc = statusColors[enr.status] || statusColors.enrolled;

              return (
                <div key={enr._id} style={{ borderBottom: '1px solid var(--border-2)' }}>
                  <div
                    onClick={() => setCdpExpanded(isExpanded ? null : enr._id)}
                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 20px', cursor: 'pointer', userSelect: 'none' }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px', background: sc.bg, color: sc.color, textTransform: 'capitalize', flexShrink: 0 }}>
                        {enr.status.replace('_', ' ')}
                      </span>
                      <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--navy)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {docTitle}{scenarioCode ? ` — ${scenarioCode}` : ''}
                      </span>
                      {enr.attemptNumber > 1 && (
                        <span style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', background: 'var(--bg)', border: '1px solid var(--border)', padding: '2px 6px', borderRadius: '4px', flexShrink: 0 }}>
                          Attempt {enr.attemptNumber}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
                      {enr.assessment?.totalScore != null && (
                        <span style={{ fontSize: '14px', fontWeight: '800', color: enr.assessment.totalScore >= 75 ? '#065f46' : '#991b1b' }}>
                          {enr.assessment.totalScore}/100
                        </span>
                      )}
                      <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                        {new Date(enr.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                      </span>
                      <span style={{ fontSize: '12px', fontWeight: '700', color: 'var(--navy-2)' }}>{isExpanded ? '▲' : '▼'}</span>
                    </div>
                  </div>

                  {isExpanded && (
                    <div style={{ padding: '0 20px 16px' }}>
                      {/* Warm-up results */}
                      {enr.questionResults?.length > 0 && (
                        <div style={{ marginBottom: '14px' }}>
                          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Warm-up Questions</p>
                          {enr.questionResults.map((qr, i) => (
                            <div key={i} style={{ background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: '8px', padding: '10px 12px', marginBottom: '6px' }}>
                              <p style={{ fontSize: '12px', fontWeight: '600', color: 'var(--navy)', marginBottom: '4px' }}>{qr.question}</p>
                              <p style={{ fontSize: '12px', color: 'var(--text-2)', marginBottom: '4px' }}>Answer: {qr.answer}</p>
                              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <span style={{
                                  fontSize: '10px', fontWeight: '700', padding: '2px 8px', borderRadius: '99px',
                                  background: qr.result === 'Correct' ? '#d1fae5' : qr.result === 'Partially Correct' ? '#fef3c7' : '#fee2e2',
                                  color: qr.result === 'Correct' ? '#065f46' : qr.result === 'Partially Correct' ? '#92400e' : '#991b1b',
                                }}>{qr.result}</span>
                                {qr.feedback && <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>{qr.feedback}</span>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Assessment scoring */}
                      {enr.assessment?.scoring && (
                        <div style={{ marginBottom: '14px' }}>
                          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Assessment Scoring</p>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {Object.entries(enr.assessment.scoring).map(([key, val]) => (
                              <div key={key} style={{ background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: '8px', padding: '8px 12px', minWidth: '120px' }}>
                                <p style={{ fontSize: '10px', color: 'var(--text-3)', fontWeight: '600', textTransform: 'capitalize', marginBottom: '2px' }}>
                                  {key.replace(/([A-Z])/g, ' $1').trim()}
                                </p>
                                <p style={{ fontSize: '14px', fontWeight: '800', color: 'var(--navy)' }}>
                                  {val.awarded}<span style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: '500' }}>/{val.max}</span>
                                </p>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Overall observation */}
                      {enr.assessment?.overallObservation && (
                        <div style={{ marginBottom: '14px' }}>
                          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Overall Observation</p>
                          <p style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.6', background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: '8px', padding: '10px 12px' }}>
                            {enr.assessment.overallObservation}
                          </p>
                        </div>
                      )}

                      {/* Error list */}
                      {enr.assessment?.errorList?.length > 0 && (
                        <div style={{ marginBottom: '14px' }}>
                          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '8px' }}>Errors Found</p>
                          {enr.assessment.errorList.map((err, i) => (
                            <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'flex-start' }}>
                              <span style={{ fontSize: '10px', fontWeight: '800', color: '#991b1b', background: '#fee2e2', borderRadius: '4px', padding: '2px 6px', flexShrink: 0, marginTop: '1px' }}>{i + 1}</span>
                              <div>
                                {err.clauseSection && <p style={{ fontSize: '11px', fontWeight: '700', color: 'var(--navy)', marginBottom: '2px' }}>{err.clauseSection}</p>}
                                <p style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.5' }}>{err.errorIssue}</p>
                                {err.correctionRequired && <p style={{ fontSize: '11px', color: '#065f46', marginTop: '2px' }}>Fix: {err.correctionRequired}</p>}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Assessor comments */}
                      {enr.assessment?.assessorComments && (
                        <div>
                          <p style={{ fontSize: '10px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '6px' }}>Assessor Comments</p>
                          <p style={{ fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.6', background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: '8px', padding: '10px 12px', whiteSpace: 'pre-wrap' }}>
                            {enr.assessment.assessorComments}
                          </p>
                        </div>
                      )}

                      {/* Submitted file */}
                      {enr.fileName && (
                        <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontSize: '11px', color: 'var(--text-3)', fontWeight: '600' }}>Submitted:</span>
                          <span style={{ fontSize: '12px', color: 'var(--navy)', fontWeight: '600' }}>{enr.fileName}</span>
                          {enr.submittedAt && (
                            <span style={{ fontSize: '11px', color: 'var(--text-3)' }}>
                              on {new Date(enr.submittedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Chat history */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <div style={s.cardHeaderLeft}>
            <h2 style={s.cardTitle}>Chat History</h2>
            <span style={s.countBadge}>{chats.length}</span>
          </div>
        </div>

        {chats.length === 0 ? (
          <p style={s.emptyText}>No chats yet.</p>
        ) : (
          <div style={s.chatList}>
            {visibleChats.map((chat, i) => (
              <div key={chat._id || i} style={s.chatItem}>
                <div style={s.chatQ}>
                  <span style={s.chatQBadge}>Q</span>
                  <p style={s.chatQText}>{chat.question}</p>
                  <span style={s.chatTs}>{new Date(chat.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                </div>
                <div style={s.chatA}>
                  <span style={s.chatABadge}>A</span>
                  <p style={s.chatAText}>{chat.answer}</p>
                </div>
              </div>
            ))}
            {chats.length > 5 && (
              <button onClick={() => setChatExpanded(!chatExpanded)} style={s.expandBtn}>
                {chatExpanded ? '▲ Show less' : `▼ Show all ${chats.length} messages`}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Token management card */}
      <div style={s.card}>
        <div style={s.cardHeader}>
          <div style={s.cardHeaderLeft}>
            <h2 style={s.cardTitle}>Token Balance</h2>
          </div>
          <div style={s.balanceDisplay}>
            <span style={{ ...s.balanceNum, color: balanceEmpty ? 'var(--danger)' : balanceLow ? 'var(--warning)' : 'var(--success)' }}>
              {(user.tokenBalance || 0).toLocaleString()}
            </span>
            <span style={s.balanceUnit}>tokens</span>
            {balanceEmpty && <span style={s.badgeDanger}>Exhausted</span>}
            {balanceLow && !balanceEmpty && <span style={s.badgeWarn}>Low</span>}
          </div>
        </div>

        {/* Grant tokens form */}
        <div style={s.grantSection}>
          <p style={s.grantTitle}>Award Tokens</p>
          <form onSubmit={handleGrantTokens} style={s.grantForm}>
            <input
              type="number"
              min="1"
              max="10000000"
              value={tokenAmount}
              onChange={(e) => setTokenAmount(e.target.value)}
              placeholder="Token amount (e.g. 50000)"
              style={s.grantInput}
              className="nice-input"
            />
            <input
              type="text"
              value={tokenNote}
              onChange={(e) => setTokenNote(e.target.value)}
              placeholder="Note (optional)"
              style={{ ...s.grantInput, flex: 1.5 }}
              className="nice-input"
            />
            <button type="submit" disabled={grantLoading || !tokenAmount} style={s.grantBtn}>
              {grantLoading ? 'Granting...' : 'Grant Tokens'}
            </button>
          </form>
          {grantMsg && <p style={s.grantSuccess}>{grantMsg}</p>}
          {grantError && <p style={s.grantErr}>{grantError}</p>}
        </div>

        {/* Token transaction log */}
        {tokenTxns.length > 0 && (
          <div style={s.txnSection}>
            <p style={s.txnSectionTitle}>Token History</p>
            {tokenTxns.map((t) => (
              <div key={t._id} style={s.txnRow}>
                <div style={{ ...s.txnIcon, background: t.type === 'credit' ? 'var(--success-bg)' : 'var(--danger-bg)', color: t.type === 'credit' ? 'var(--success)' : 'var(--danger)' }}>
                  {t.type === 'credit' ? '+' : '−'}
                </div>
                <div style={s.txnMid}>
                  <p style={s.txnTitle}>
                    {t.source === 'admin_grant' && 'Admin grant'}
                    {t.source === 'purchase' && 'Purchase'}
                    {t.source === 'chat_usage' && 'Query usage'}
                    {t.source === 'admin_grant' && t.addedBy && (
                      <span style={s.txnBy}> · by {t.addedBy.name || t.addedBy.phone}</span>
                    )}
                  </p>
                  {t.note && <p style={s.txnNote}>{t.note}</p>}
                </div>
                <div style={s.txnRight}>
                  <p style={{ ...s.txnAmount, color: t.type === 'credit' ? 'var(--success)' : 'var(--danger)' }}>
                    {t.type === 'credit' ? '+' : '−'}{t.amount.toLocaleString()}
                  </p>
                  <p style={s.txnBalance}>{t.balanceAfter.toLocaleString()} after</p>
                  <p style={s.txnDate}>{new Date(t.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</p>
                </div>
              </div>
            ))}
          </div>
        )}
        {tokenTxns.length === 0 && (
          <p style={{ padding: '0 20px 16px', fontSize: '13px', color: 'var(--text-3)' }}>No token transactions yet.</p>
        )}
      </div>
    </div>
  );
}


function roleColor(role) {
  if (role === 'superadmin') return { background: 'var(--danger)', color: '#fff' };
  if (role === 'admin') return { background: 'var(--warning)', color: '#fff' };
  return { background: 'var(--navy)', color: 'var(--gold-mid)' };
}

const s = {
  page: { maxWidth: '800px', margin: '0 auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: '16px' },
  center: { display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh' },
  spinner: { width: '36px', height: '36px', borderRadius: '50%', border: '3px solid var(--border)', borderTop: '3px solid var(--navy)', animation: 'spin 0.8s linear infinite' },
  header: { display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  back: { background: 'none', border: 'none', color: 'var(--navy)', fontWeight: '700', fontSize: '13px', cursor: 'pointer', padding: '0', display: 'flex', alignItems: 'center', gap: '4px' },
  headerBadge: { fontSize: '11px', background: 'var(--gold-light)', color: 'var(--gold)', fontWeight: '700', padding: '4px 12px', borderRadius: '99px', border: '1px solid var(--gold-mid)' },

  profileCard: { borderRadius: '16px', overflow: 'hidden', background: '#fff', border: '1px solid var(--border)', boxShadow: 'var(--shadow)', position: 'relative' },
  profileBg: { height: '70px', background: 'var(--navy)' },
  profileContent: { padding: '0 20px 20px', display: 'flex', alignItems: 'flex-end', gap: '16px', marginTop: '-28px' },
  avatar: { width: '56px', height: '56px', borderRadius: '50%', background: 'var(--navy-2)', color: 'var(--gold-mid)', fontFamily: "'Playfair Display', Georgia, serif", fontSize: '20px', fontWeight: '700', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid #fff', flexShrink: 0, boxShadow: 'var(--shadow)' },
  profileInfo: { paddingTop: '30px', flex: 1 },
  profileName: { fontSize: '18px', fontWeight: '700', color: 'var(--navy)', marginBottom: '2px' },
  profilePhone: { fontSize: '12px', color: 'var(--text-3)', marginBottom: '4px' },
  badgeRow: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' },
  roleBadge: { fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px', textTransform: 'capitalize' },
  joinedBadge: { fontSize: '11px', color: 'var(--text-3)', fontWeight: '500' },
  disabledBadge: { fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px', background: 'var(--danger-bg)', color: 'var(--danger)' },
  disableAccountBtn: { marginTop: '8px', background: '#fff', color: 'var(--danger)', border: '1px solid rgba(185,28,28,0.3)', padding: '7px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans', sans-serif" },
  enableAccountBtn: { marginTop: '8px', background: 'var(--success)', color: '#fff', border: 'none', padding: '7px 16px', borderRadius: '8px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', fontFamily: "'DM Sans', sans-serif" },

  statsRow: { display: 'flex', gap: '10px', flexWrap: 'wrap' },
  statCard: { flex: '1 1 140px', background: '#fff', border: '1px solid var(--border)', borderRadius: '14px', padding: '14px', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: 'var(--shadow-sm)' },
  statIcon: { fontSize: '20px' },
  statLabel: { fontSize: '10px', color: 'var(--text-3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  statVal: { fontSize: '15px', fontWeight: '800', color: 'var(--navy)', marginTop: '2px' },

  card: { background: '#fff', border: '1px solid var(--border)', borderRadius: '16px', overflow: 'hidden', boxShadow: 'var(--shadow-sm)' },
  cardHeader: { padding: '16px 20px', borderBottom: '1px solid var(--border-2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '10px' },
  cardHeaderLeft: { display: 'flex', alignItems: 'center', gap: '8px' },
  cardTitle: { fontSize: '14px', fontWeight: '700', color: 'var(--navy)' },
  countBadge: { background: 'var(--gold-light)', color: 'var(--gold)', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '99px', border: '1px solid var(--gold-mid)' },
  emptyText: { padding: '20px', color: 'var(--text-3)', fontSize: '13px' },

  balanceDisplay: { display: 'flex', alignItems: 'center', gap: '8px' },
  balanceNum: { fontSize: '22px', fontWeight: '800' },
  balanceUnit: { fontSize: '12px', color: 'var(--text-3)' },
  badgeDanger: { background: 'var(--danger-bg)', color: 'var(--danger)', fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px' },
  badgeWarn: { background: 'var(--warning-bg)', color: 'var(--warning)', fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '99px' },

  grantSection: { padding: '16px 20px', borderBottom: '1px solid var(--border-2)' },
  grantTitle: { fontSize: '12px', fontWeight: '700', color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '10px' },
  grantForm: { display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' },
  grantInput: { flex: 1, minWidth: '140px', padding: '9px 12px', border: '1px solid var(--border)', borderRadius: '8px', fontSize: '13px', background: 'var(--bg)', color: 'var(--text)' },
  grantBtn: { background: 'var(--navy)', color: '#fff', border: 'none', padding: '9px 18px', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', whiteSpace: 'nowrap' },
  grantSuccess: { fontSize: '13px', color: 'var(--success)', marginTop: '8px', fontWeight: '600' },
  grantErr: { fontSize: '13px', color: 'var(--danger)', marginTop: '8px' },

  txnSection: { borderTop: '1px solid var(--border-2)' },
  txnSectionTitle: { fontSize: '11px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 20px 4px' },
  txnRow: { display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 20px', borderBottom: '1px solid var(--border-2)' },
  txnIcon: { width: '28px', height: '28px', borderRadius: '7px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: '800', flexShrink: 0 },
  txnMid: { flex: 1, minWidth: 0 },
  txnTitle: { fontSize: '12px', fontWeight: '600', color: 'var(--navy)' },
  txnBy: { color: 'var(--text-3)', fontWeight: '400' },
  txnNote: { fontSize: '11px', color: 'var(--text-3)', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  txnRight: { textAlign: 'right', flexShrink: 0 },
  txnAmount: { fontSize: '13px', fontWeight: '800' },
  txnBalance: { fontSize: '10px', color: 'var(--text-3)', marginTop: '1px' },
  txnDate: { fontSize: '10px', color: 'var(--text-3)', marginTop: '1px' },

  resumeTabs: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  resumeTab: { background: 'var(--bg)', border: '1px solid var(--border)', color: 'var(--text-2)', padding: '4px 12px', borderRadius: '99px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  resumeTabActive: { background: 'var(--navy)', color: '#fff', border: '1px solid var(--navy)' },
  resumeBody: { padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: '20px' },
  resumeSection: { display: 'flex', flexDirection: 'column', gap: '10px' },
  resumeSectionTitle: { fontSize: '10px', fontWeight: '800', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', borderBottom: '1px solid var(--border-2)', paddingBottom: '6px' },
  infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '8px' },
  infoItem: { display: 'flex', flexDirection: 'column', gap: '2px' },
  infoLabel: { fontSize: '11px', color: 'var(--text-3)', fontWeight: '600' },
  infoVal: { fontSize: '13px', color: 'var(--navy)', fontWeight: '600' },
  summaryText: { fontSize: '13px', color: 'var(--text-2)', lineHeight: '1.6' },
  expCard: { background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: '10px', padding: '12px 14px' },
  expHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '10px' },
  expPosition: { fontSize: '13px', fontWeight: '700', color: 'var(--navy)' },
  expCompany: { fontSize: '12px', color: 'var(--navy-2)', fontWeight: '600' },
  expDates: { fontSize: '11px', color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 },
  expDesc: { fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.6', marginTop: '8px' },
  skillsWrap: { display: 'flex', flexWrap: 'wrap', gap: '6px' },
  skillTag: { background: 'var(--gold-light)', color: 'var(--gold)', fontSize: '12px', fontWeight: '600', padding: '4px 12px', borderRadius: '99px', border: '1px solid var(--gold-mid)' },

  assessBody: { display: 'flex', flexDirection: 'column', gap: '0' },
  assessResult: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px', padding: '16px 20px', background: 'var(--gold-light)', borderBottom: '1px solid var(--border-2)', flexWrap: 'wrap' },
  assessResultLeft: { display: 'flex', flexDirection: 'column', gap: '4px' },
  assessOutcomeCode: { fontSize: '10px', fontWeight: '800', color: 'var(--gold)', textTransform: 'uppercase', letterSpacing: '1px' },
  assessTitle: { fontSize: '15px', fontWeight: '700', color: 'var(--navy)', lineHeight: '1.3' },
  assessArea: { fontSize: '12px', color: 'var(--navy-2)', fontWeight: '600' },
  assessScores: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  assessScoreItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' },
  assessScoreVal: { fontSize: '18px', fontWeight: '800', color: 'var(--navy)' },
  assessScoreLabel: { fontSize: '10px', color: 'var(--text-3)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' },
  qaSection: { padding: '0' },
  qaSectionTitle: { fontSize: '11px', fontWeight: '700', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '10px 20px 4px' },
  qaRow: { display: 'flex', gap: '12px', padding: '10px 20px', borderBottom: '1px solid var(--border-2)', alignItems: 'flex-start' },
  qaNumWrap: { flexShrink: 0, paddingTop: '1px' },
  qaNum: { fontSize: '10px', fontWeight: '800', color: 'var(--text-3)', background: 'var(--bg)', border: '1px solid var(--border-2)', borderRadius: '5px', padding: '2px 6px' },
  qaContent: { flex: 1, minWidth: 0 },
  qaQuestion: { fontSize: '12px', fontWeight: '600', color: 'var(--navy)', lineHeight: '1.5', marginBottom: '5px' },
  qaAnswer: { display: 'flex', alignItems: 'flex-start', gap: '7px' },
  qaAnswerId: { fontSize: '10px', fontWeight: '800', color: '#fff', background: 'var(--navy)', borderRadius: '4px', padding: '2px 6px', flexShrink: 0, marginTop: '1px' },
  qaAnswerText: { fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.5' },
  qaNoAnswer: { fontSize: '11px', color: 'var(--text-3)', fontStyle: 'italic' },

  chatList: { padding: '0 0 8px' },
  chatItem: { borderBottom: '1px solid var(--border-2)', padding: '12px 20px', display: 'flex', flexDirection: 'column', gap: '8px' },
  chatQ: { display: 'flex', gap: '10px', alignItems: 'flex-start' },
  chatQBadge: { background: 'var(--navy)', color: 'var(--gold-mid)', fontSize: '9px', fontWeight: '800', width: '18px', height: '18px', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px' },
  chatQText: { flex: 1, fontSize: '13px', fontWeight: '600', color: 'var(--navy)', lineHeight: '1.5' },
  chatTs: { fontSize: '10px', color: 'var(--text-3)', whiteSpace: 'nowrap', flexShrink: 0 },
  chatA: { display: 'flex', gap: '10px', alignItems: 'flex-start', paddingLeft: '4px' },
  chatABadge: { background: 'var(--bg)', color: 'var(--text-3)', fontSize: '9px', fontWeight: '800', width: '18px', height: '18px', borderRadius: '5px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '2px', border: '1px solid var(--border)' },
  chatAText: { flex: 1, fontSize: '12px', color: 'var(--text-2)', lineHeight: '1.6' },
  expandBtn: { display: 'block', width: '100%', padding: '12px', background: 'none', border: 'none', color: 'var(--navy-2)', fontWeight: '700', fontSize: '13px', cursor: 'pointer', textAlign: 'center', borderTop: '1px solid var(--border-2)' },
};
